import { Component } from "@theme/component";

/**
 * @typedef {Object} Options
 * @property {boolean} [childrenOnly] - Only update children
 * @property {(node: Node | undefined) => string|number|undefined} [getNodeKey] - Get node key for matching
 * @property {(oldNode: Node, newNode: Node) => void} [onBeforeUpdate] - Pre-update hook
 * @property {(node: Node) => void} [onAfterUpdate] - Post-update hook
 * @property {(oldNode: Node, newNode: Node) => boolean} [reject] - Reject a node from being morphed
 * @property {boolean} [hydrationMode] - If true, only morph subtrees whose elements have `data-hydration-key="<non-empty>"`, matched by that value
 */

const HYDRATION_KEY_ATTRIBUTE = "data-hydration-key";

/**
 * The options for the morph
 * @type {Options}
 */
export const MORPH_OPTIONS = {
  childrenOnly: true,
  hydrationMode: false,
  reject(oldNode, newNode) {
    if (newNode.nodeType === Node.TEXT_NODE && newNode.nodeValue?.trim() === "") {
      return true;
    }

    if (
      newNode instanceof HTMLTemplateElement &&
      newNode.shadowRootMode === "open" &&
      oldNode.parentElement?.tagName === newNode.parentElement?.tagName &&
      oldNode.parentElement?.shadowRoot !== null
    ) {
      // Ignore template elements of components that are already initialized
      return true;
    }

    if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === "shopify:rendered_by_section_api") {
      // Remove a comment node injected by the Section Rendering API in the Theme Editor
      return true;
    }

    return false;
  },
  onBeforeUpdate(oldNode, newNode) {
    if (!(oldNode instanceof Element && newNode instanceof Element)) {
      return;
    }

    const attributes = ["data-current-checked", "data-previous-checked", "cart-summary-sticky"];

    for (const attribute of attributes) {
      const oldValue = oldNode.getAttribute(attribute);
      const newValue = newNode.getAttribute(attribute);

      if (oldValue && oldValue !== newValue) {
        newNode.setAttribute(attribute, oldValue);
      }
    }

    // Special case for elements that need to keep their style
    const elements = ["fieldset.variant-option"];
    const ids = ["account-popover"];

    for (const element of elements) {
      if (oldNode.matches(element) && newNode.matches(element)) {
        const oldStyle = oldNode.getAttribute("style");
        if (oldStyle) {
          newNode.setAttribute("style", oldStyle);
        }
      }
    }
    for (const id of ids) {
      if (oldNode.id === id && newNode.id === id) {
        const oldStyle = oldNode.getAttribute("style");
        if (oldStyle) {
          newNode.setAttribute("style", oldStyle);
        }
      }
    }

    // Preserve temporary view transition name
    if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement && oldNode.style.viewTransitionName) {
      newNode.style.viewTransitionName = oldNode.style.viewTransitionName;
    }
  },
  onAfterUpdate(node) {
    if (node instanceof Component) {
      queueMicrotask(() => node.updatedCallback());
    }
  },
};

/**
 * Morphs one DOM tree into another by comparing nodes and applying minimal changes
 * @param {Node} oldTree - The existing DOM tree
 * @param {Node | string} newTree - The new DOM tree to morph to
 * @param {Options} [options] - Configuration options
 * @returns {Node} The morphed DOM tree
 */
export function morph(oldTree, newTree, options = MORPH_OPTIONS) {
  if (!oldTree || !newTree) {
    throw new Error("Both oldTree and newTree must be provided");
  }

  let finalNewTree = newTree;
  if (typeof finalNewTree === "string") {
    const parsedNewTree = new DOMParser().parseFromString(finalNewTree, "text/html").body.firstChild;
    if (!parsedNewTree) {
      throw new Error("newTree string is not valid HTML");
    }
    finalNewTree = parsedNewTree;
  }

  if (options.hydrationMode && oldTree instanceof Element && finalNewTree instanceof Element) {
    morphHydrationByKey(oldTree, finalNewTree, options);
    return oldTree;
  }

  if (options.childrenOnly) {
    updateChildren(finalNewTree, oldTree, options);
    return oldTree;
  }

  if (finalNewTree.nodeType === 11) {
    throw new Error("newTree should have one root node (not a DocumentFragment)");
  }

  return walk(finalNewTree, oldTree, options);
}

/**
 * Collect targets under a root element that have a non-empty key for the attribute.
 * Includes the root itself if it matches.
 *
 * @param {Element} root
 * @returns {Element[]}
 */
function collectHydrationTargets(root) {
  const targets = [];
  if (root.hasAttribute(HYDRATION_KEY_ATTRIBUTE)) {
    targets.push(root);
  }
  targets.push(...root.querySelectorAll(`[${HYDRATION_KEY_ATTRIBUTE}]`));
  return targets;
}

/**
 * Morph only keyed targets from `newRoot` into `oldRoot` (a.k.a. "keyed lazy hydration").
 *
 * Philosophy:
 * - This updates the *contents* of pre-existing targets. We intentionally do NOT insert new targets (or remove missing ones).
 * - By requiring targets to already exist in `oldRoot`, we preserve runtime state that may already
 *   be attached to the existing DOM (custom elements, listeners, focus, transient UI state) and preserve the layout and UI behavior.
 * - Intended use-case: avoid expensive server-side rendering operations in the initial render, and hydrate targeted sections after page load. e.g. Querying all product and collection drops in off-screen menus.
 *
 * Contract:
 * - An element is eligible only if it has `data-hydration-key="<non-empty>"`.
 * - Matching uses ONLY that key value (no fallbacks) to avoid accidental cross-updates.
 * - Once a target is matched, we run a normal morph *within that target* (attributes + children).
 *
 * @param {Element} oldRoot
 * @param {Element} newRoot
 * @param {Options} options
 */
function morphHydrationByKey(oldRoot, newRoot, options) {
  const oldTargets = collectHydrationTargets(oldRoot);
  const newTargets = collectHydrationTargets(newRoot);

  /** @type {Map<string, Element[]>} */
  const oldTargetsByKey = new Map();

  for (const oldTarget of oldTargets) {
    const key = oldTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);

    if (key !== null && key !== "") {
      const existing = oldTargetsByKey.get(key) ?? [];
      existing.push(oldTarget);
      oldTargetsByKey.set(key, existing);
    }
  }

  for (const newTarget of newTargets) {
    const key = newTarget.getAttribute(HYDRATION_KEY_ATTRIBUTE);

    if (key !== null && key !== "") {
      const matches = oldTargetsByKey.get(key);
      const oldTarget = matches?.shift();

      if (oldTarget) {
        // For keyed targets we want attribute updates as well, regardless of the caller's childrenOnly default.
        morph(oldTarget, newTarget, {
          ...options,
          hydrationMode: false,
          childrenOnly: false,
        });
      }
    }
  }
}

/**
 * Whether a node carries one of the data-skip-* morph opt-out attributes. hasAttribute (not
 * dataset) so the contract works on every Element — SVG/MathML carriers included — without
 * narrowing to HTMLElement (which would silently drop them).
 * @param {Node} node - The node to check.
 * @param {string} attribute - The data-skip-* attribute name.
 * @returns {boolean} Whether the attribute is present.
 */
const hasSkipAttribute = (node, attribute) => node instanceof Element && node.hasAttribute(attribute);

/**
 * Walk and morph a dom tree
 * @param {Node} newNode - The new node to morph to
 * @param {Node} oldNode - The old node to morph from
 * @param {Options} options - The options object
 * @returns {Node} The new node or the morphed old node
 */
function walk(newNode, oldNode, options) {
  // Skip morphing if there is no old or new node
  if (!oldNode) {
    return newNode;
  }
  if (!newNode) {
    return oldNode;
  }

  // Skip morphing if nodes are identical
  if (newNode.isSameNode?.(oldNode)) {
    return oldNode;
  }

  // Check node type and tag name first
  if (newNode.nodeType !== oldNode.nodeType) {
    return newNode;
  }
  if (newNode instanceof Element && oldNode instanceof Element) {
    // Skip morphing if the node is shopify-accelerated-checkout-cart https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout#implement-accelerated-checkout-buttons-on-cart
    if (oldNode.tagName === "SHOPIFY-ACCELERATED-CHECKOUT-CART") {
      return oldNode;
    }

    if (newNode.tagName !== oldNode.tagName) {
      return newNode;
    }

    // Only check keys for elements, and only if both nodes have keys
    const newKey = getNodeKey(newNode, options);
    const oldKey = getNodeKey(oldNode, options);
    if (newKey && oldKey && newKey !== oldKey) {
      return newNode;
    }
  }

  // We can morph, update the node and its children
  if (hasSkipAttribute(oldNode, "data-skip-node-update") && hasSkipAttribute(newNode, "data-skip-node-update")) {
    // This is a special case where we don't want to morph the node, but we want to morph the children
    updateChildren(newNode, oldNode, options);
  } else {
    updateNode(newNode, oldNode, options);
    updateChildren(newNode, oldNode, options);
  }

  options.onAfterUpdate?.(oldNode);

  return oldNode;
}

/**
 * Core morphing function that updates attributes and special elements
 * @param {Node} newNode - Source node with desired state
 * @param {Node} oldNode - Target node to update
 * @param {Options} options - The options object
 */
function updateNode(newNode, oldNode, options) {
  options.onBeforeUpdate?.(oldNode, newNode);

  if (
    ((newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement) ||
      (newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement)) &&
    !newNode.hasAttribute("declarative-open")
  ) {
    newNode.open = oldNode.open;
  }

  if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement) {
    for (const attr of ["slot", "sizes"]) {
      const oldValue = oldNode.getAttribute(attr);
      const newValue = newNode.getAttribute(attr);

      if (oldValue !== newValue) {
        if (oldValue === null) {
          newNode.removeAttribute(attr);
        } else {
          newNode.setAttribute(attr, oldValue);
        }
      }
    }
  }

  if (newNode instanceof Element && oldNode instanceof Element) {
    if (!oldNode.isEqualNode(newNode)) {
      copyAttributes(newNode, oldNode);
    }
  } else if ((newNode instanceof Text || newNode instanceof Comment) && oldNode.nodeValue !== newNode.nodeValue) {
    oldNode.nodeValue = newNode.nodeValue;
  } else {
    // Node is neither an Element nor a Text/Comment node requiring value update — no action needed
  }

  // Handle special elements
  if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
    updateInput(newNode, oldNode);
  } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
    updateAttribute(newNode, oldNode, "selected");
  } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
    updateTextarea(newNode, oldNode);
  } else {
    // Node is not a special input, option, or textarea element — no special handling needed
  }
}

/**
 * Gets a node's key using the getNodeKey option if provided
 * @param {Node | undefined} node - The node to get the key from
 * @param {Options} [options] - The options object that may contain getNodeKey
 * @returns {string|number|undefined} The node's key if one exists
 */
function getNodeKey(node, options) {
  return options?.getNodeKey?.(node) ?? (node instanceof Element ? node.id : undefined);
}

/**
 * Updates a boolean attribute and its corresponding property on an element
 * @param {any} newNode - The new element
 * @param {any} oldNode - The existing element to update
 * @param {string} name - The name of the attribute/property to update
 */
function updateAttribute(newNode, oldNode, name) {
  if (newNode[name] === oldNode[name]) {
    return;
  }

  oldNode[name] = newNode[name];
  if (newNode[name] === null) {
    oldNode.removeAttribute(name);
  } else {
    oldNode.setAttribute(name, "");
  }
}

/**
 * Copies attributes from a new node to an old node, handling namespaced attributes
 * @param {Element} newNode - The new node to copy attributes from
 * @param {Element} oldNode - The existing node to update attributes on
 */
function copyAttributes(newNode, oldNode) {
  const oldAttrs = oldNode.attributes;
  const newAttrs = newNode.attributes;

  // Update or add new attributes
  for (const attr of newAttrs) {
    const { name: attrName, namespaceURI: attrNamespaceURI, value: attrValue } = attr;
    const localName = attr.localName || attrName;

    // Update all attributes except resource attributes whose value has not changed (to prevent unnecessary network requests)
    const isUnchangedResourceAttr =
      (attrName === "src" || attrName === "href" || attrName === "srcset" || attrName === "poster") &&
      oldNode.getAttribute(attrName) === attrValue;

    if (!isUnchangedResourceAttr) {
      if (attrNamespaceURI) {
        const fromValue = oldNode.getAttributeNS(attrNamespaceURI, localName);
        if (fromValue !== attrValue) {
          oldNode.setAttributeNS(attrNamespaceURI, localName, attrValue);
        }
      } else if (oldNode.hasAttribute(attrName)) {
        const fromValue = oldNode.getAttribute(attrName);
        if (fromValue !== attrValue) {
          if (attrValue === "null" || attrValue === "undefined") {
            oldNode.removeAttribute(attrName);
          } else {
            oldNode.setAttribute(attrName, attrValue);
          }
        }
      } else {
        oldNode.setAttribute(attrName, attrValue);
      }
    }
  }

  // Remove old attributes not present in new node
  for (const attr of oldAttrs) {
    const { name: attrName, namespaceURI: attrNamespaceURI } = attr;
    const localName = attr.localName || attrName;

    if (attrNamespaceURI) {
      if (!newNode.hasAttributeNS(attrNamespaceURI, localName)) {
        oldNode.removeAttributeNS(attrNamespaceURI, localName);
      }
    } else if (newNode.hasAttribute(attrName)) {
      // Attribute exists in both nodes — no removal needed
    } else {
      oldNode.removeAttribute(attrName);
    }
  }
}

/**
 * Updates special properties and attributes on input elements
 * Handles checked, disabled, indeterminate states and value
 * @param {HTMLInputElement} newNode - The new input element
 * @param {HTMLInputElement} oldNode - The existing input element to update
 */
function updateInput(newNode, oldNode) {
  const newValue = newNode.value;

  updateAttribute(newNode, oldNode, "checked");
  updateAttribute(newNode, oldNode, "disabled");

  // Handle indeterminate state (cannot be set via HTML attribute)
  if (newNode.indeterminate !== oldNode.indeterminate) {
    oldNode.indeterminate = newNode.indeterminate;
  }

  // Skip file inputs since they can't be changed programmatically
  if (oldNode.type === "file") {
    return;
  }

  if (newValue !== oldNode.value) {
    oldNode.setAttribute("value", newValue);
    oldNode.value = newValue;
  }

  if (newValue === "null") {
    oldNode.value = "";
    oldNode.removeAttribute("value");
  }

  if (!newNode.hasAttributeNS(null, "value")) {
    oldNode.removeAttribute("value");
  } else if (oldNode.type === "range") {
    // Update range input UI
    oldNode.value = newValue;
  } else {
    // Neither condition applies — no value attribute update needed
  }
}

/**
 * Updates the value of a textarea element
 * @param {HTMLTextAreaElement} newNode - The new textarea element
 * @param {HTMLTextAreaElement} oldNode - The existing textarea element to update
 */
function updateTextarea(newNode, oldNode) {
  const newValue = newNode.value;
  if (newValue !== oldNode.value) {
    oldNode.value = newValue;
  }

  const firstChild = oldNode.firstChild;
  if (firstChild?.nodeType === Node.TEXT_NODE) {
    if (newValue === "" && firstChild.nodeValue === oldNode.placeholder) {
      return;
    }
    firstChild.nodeValue = newValue;
  }
}

/**
 * If app scripts store references to the DOM on initialization, they will be invalidated by the morph because browsers don't re-execute them.
 * This function removes and recreates them to force re-execution.
 * @param {Element} container - The container element to search for app block scripts
 */
function recreateAppBlockScripts(container) {
  const scripts = container.querySelectorAll(".shopify-app-block script[src]");

  for (const script of scripts) {
    if (script instanceof HTMLScriptElement) {
      const parent = script.parentElement;

      if (parent) {
        const newScript = document.createElement("script");
        for (const attr of script.attributes) {
          newScript.setAttribute(attr.name, attr.value);
        }
        if (script.textContent) {
          newScript.textContent = script.textContent;
        }

        script.remove();
        parent.append(newScript);
      }
    }
  }
}

/**
 * @param {NodeListOf<ChildNode> | ChildNode[]} childNodes
 * @param {number} startIndex
 * @param {ChildNode} newChild
 * @param {Options} options
 * @returns {ChildNode | null}
 */
function findMatchingNode(childNodes, startIndex, newChild, options) {
  for (let j = startIndex; j < childNodes.length; j++) {
    const potentialOldNode = childNodes[j];

    if (potentialOldNode && same(potentialOldNode, newChild, options)) {
      return potentialOldNode;
    }
  }
  return null;
}

/**
 * Morph a new child node against an old child when no direct match is found.
 * Attempts to find a keyed match further in the old list; falls back to in-place
 * morphing or insertion.
 * @param {Node} oldNode - The parent node containing the old children
 * @param {ChildNode} oldChild - The current old child at this position
 * @param {ChildNode} newChild - The new child to reconcile
 * @param {number} i - Current loop index (used to search forward for a match)
 * @param {number} offset - Current offset between new and old child indices
 * @param {Options} options - The morph options object
 * @returns {number} Updated offset after reconciliation
 */
function morphUnmatchedNodes(oldNode, oldChild, newChild, i, offset, options) {
  const oldMatch = findMatchingNode(oldNode.childNodes, i, newChild, options);
  let newOffset = offset;

  if (oldMatch) {
    const morphed = walk(newChild, oldMatch, options);

    if (morphed !== oldMatch) {
      newOffset++;
    }

    oldChild.before(morphed);
  } else if (!getNodeKey(newChild, options) && !getNodeKey(oldChild, options)) {
    const morphed = walk(newChild, oldChild, options);

    if (morphed !== oldChild) {
      oldChild.replaceWith(morphed);
      newOffset++;
    }
  } else {
    oldChild.before(newChild);
    newOffset++;
  }

  return newOffset;
}

/**
 * Update the children of elements
 * @param {Node} newNode - The new node to update children on
 * @param {Node} oldNode - The existing node to update children on
 * @param {Options} options - The options object
 */
function updateChildren(newNode, oldNode, options) {
  if (hasSkipAttribute(oldNode, "data-skip-subtree-update") && hasSkipAttribute(newNode, "data-skip-subtree-update")) {
    return;
  }

  let offset = 0;
  let i = 0;

  while (true) {
    const oldChild = oldNode.childNodes[i];
    const newChild = newNode.childNodes[i - offset];

    // Both nodes are empty, do nothing
    if (!oldChild && !newChild) {
      break;
    }

    // There is no new child, remove old; do not advance i so the same slot is re-evaluated
    if (!newChild) {
      if (oldChild) {
        oldChild.remove();
      }
    } else if (!oldChild) {
      // There is no old child, add new
      /** @type {ParentNode} */ (oldNode).append(newChild);
      offset++;
      i++;
    } else if (same(newChild, oldChild, options)) {
      // Both nodes are the same, morph
      const morphed = walk(newChild, oldChild, options);

      if (morphed !== oldChild) {
        oldChild.replaceWith(morphed);
        offset++;
      }

      i++;
    } else if (options.reject?.(oldChild, newChild)) {
      // Rejected node: remove new child and re-evaluate the same slot
      newChild.remove();
    } else {
      // Try to find a matching node to reorder
      offset = morphUnmatchedNodes(oldNode, oldChild, newChild, i, offset, options);
      i++;
    }
  }

  // Recreate app block scripts to bypass browser script deduplication
  if (oldNode instanceof Element) {
    recreateAppBlockScripts(oldNode);
  }
}

/**
 * Check if two nodes are the same
 * @param {Node} nodeA - The first node
 * @param {Node} nodeB - The second node
 * @param {Options} options - The options object
 * @returns {boolean} True if the nodes are the same, false otherwise
 */
function same(nodeA, nodeB, options) {
  // If node types don't match, they're not the same
  if (nodeA.nodeType !== nodeB.nodeType) {
    return false;
  }

  // For elements, check tag name first
  if (nodeA.nodeType === Node.ELEMENT_NODE) {
    if (nodeA instanceof Element && nodeB instanceof Element && nodeA.tagName !== nodeB.tagName) {
      return false;
    }

    // Only compare keys if both nodes have them
    const aKey = getNodeKey(nodeA, options);
    const bKey = getNodeKey(nodeB, options);
    if (aKey && bKey && aKey !== bKey) {
      return false;
    }
  }

  // For text/comment nodes, compare content
  if (nodeA.nodeType === Node.TEXT_NODE && nodeB.nodeType === Node.TEXT_NODE) {
    // Trim whitespace to avoid false negatives
    return nodeA.nodeValue?.trim() === nodeB.nodeValue?.trim();
  }
  if (nodeA.nodeType === Node.COMMENT_NODE && nodeB.nodeType === Node.COMMENT_NODE) {
    return nodeA.nodeValue === nodeB.nodeValue;
  }

  // If we get here and nodes are elements with same tag (and compatible keys), they're the same
  return true;
}
